import { QueryError } from '@graphql-box/helpers';
import { type Movie, ScreenType, type Tv } from '@tmdb-graphql-api/schema/types';
import { type ReactElement, useEffect } from 'react';
import { TileCarousel } from '#components/TileCarousel/index.tsx';
import { formatAndMergeMoviesAndTvResults } from '#helpers/formatAndMergeMoviesAndTvResults.ts';
import { useMovieTvMultiQuery } from '#hooks/useMovieTvMultiQuery.ts';
import { useToolbelt } from '#hooks/useToolbelt.ts';
import { useStore } from '#store/useStore.ts';
import { type ActivePreview } from '#types.ts';

export type FavoritesCarouselProps = {
  children?: (element?: ReactElement) => ReactElement | undefined;
  setActivePreview?: (item: ActivePreview) => void;
};

export const FavoritesCarousel = ({ children, setActivePreview }: FavoritesCarouselProps) => {
  const { config } = useToolbelt();
  const favorites = useStore(store => store.favorites);
  const favoriteMovies = favorites.filter(entry => entry.type === ScreenType.Movie).map(entry => entry.id);
  const favoriteTv = favorites.filter(entry => entry.type === ScreenType.Tv).map(entry => entry.id);

  const { execute, movieLoading, movieResults, tvLoading, tvResults } = useMovieTvMultiQuery({
    initiator: 'FavoritesCarousel',
  });

  const depsKey = JSON.stringify(favorites);

  useEffect(() => {
    execute(favoriteMovies, favoriteTv);
    // We only want to re-execute when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depsKey]);

  const allResults = [...movieResults, ...tvResults];

  if (allResults.length > 0 && allResults.every(result => result instanceof QueryError)) {
    console.error(allResults);
    return;
  }

  const movieData: Movie[] = [];
  const tvData: Tv[] = [];
  const errors: QueryError[] = [];

  for (const result of movieResults) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      movieData.push(result.data.movie);
    }
  }

  for (const result of tvResults) {
    if (result instanceof QueryError) {
      errors.push(result);
    } else {
      tvData.push(result.data.tv);
    }
  }

  if (errors.length > 0) {
    console.log(errors);
  }

  const items = formatAndMergeMoviesAndTvResults(movieData, tvData, config, favorites);

  if (!movieLoading && !tvLoading && items.length === 0) {
    return;
  }

  const tileCarousel = <TileCarousel items={items} setActivePreview={setActivePreview} />;
  return children ? children(tileCarousel) : tileCarousel;
};
